import { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useUIContext } from '@cccom/config/app-config';
import { AppIds } from '@cccom/config/app-type';
import { useEnvironment } from '@cccom/config/env';
import { useDevModeShortcut } from '@cccom/shared/settings';
import { isNonProdEnv } from '@cccom/shared/utils/common';
import { Button } from '@cccom/ui/src/components/shadcn/button';
import { Toaster } from '@cccom/ui/src/components/shadcn/sonner';
import { cn } from '@cccom/ui/src/utils';

import { SidebarInset, SidebarProvider } from '../../shadcn/sidebar';
import { HasValidRolesModal } from '../HasValidRolesModal';
import { CCSidebar, SidebarProps } from './CCSidebar';
import { DevModeBadge } from './DevModeBadge';
import { Topbar, TopbarProps } from './Topbar';

export type SharedLayoutProps<TApp extends AppIds> = {
  children: ReactNode;
  siteSelector?: ReactNode;
  appTierBadge?: ReactNode;
  topbar?: TopbarProps;
  sidebar?: SidebarProps<TApp>;
  className?: string;
  limitScreenSize?: boolean;
};

export function SharedLayout<TApp extends AppIds>({
  children,
  siteSelector,
  appTierBadge,
  topbar,
  sidebar,
  className,
  limitScreenSize: _limitScreenSize = true,
}: SharedLayoutProps<TApp>) {
  const [t] = useTranslation();
  const { APP } = useEnvironment();
  const { topbarConfig } = useUIContext();
  const [limitScreenSize, setLimitScreenSize] = useState(_limitScreenSize);

  const { devMode, setDevMode } = useDevModeShortcut(isNonProdEnv());

  const showTopbar = !topbarConfig.hide;

  return (
    <SidebarProvider>
      <HasValidRolesModal />

      {devMode && <DevModeBadge setDevMode={setDevMode} />}

      {limitScreenSize && (
        <section className="grid h-screen w-screen items-center justify-center gap-7 lg:hidden">
          <div className="grid place-items-center gap-7 p-7 text-center">
            {APP?.logo && (
              <span>
                <img src={APP?.logo} alt="app logo" height="40px" />
              </span>
            )}
            <p>
              {t('common.errors.screen_too_small', {
                replace: [t(`${APP?.name}`)],
              })}
            </p>
            <Button
              onClick={() => setLimitScreenSize(false)}
              aria-label={t('common.actions.continue_anyway')}
            >
              {t('common.actions.continue_anyway')}
            </Button>
          </div>
        </section>
      )}

      <div
        className={cn('w-full lg:flex', limitScreenSize ? 'hidden' : 'flex')}
      >
        <div className="relative">{sidebar && <CCSidebar {...sidebar} />}</div>

        <SidebarInset>
          <header className={cn('sticky top-0 z-40')}>
            <Topbar
              {...topbar}
              logoProps={sidebar?.logoProps}
              hasSidebar={!!sidebar}
              siteSelector={siteSelector}
              appTierBadge={appTierBadge}
              className={showTopbar ? undefined : 'hidden'}
            />
          </header>

          <main className="h-full w-full">
            <div
              id="shared-content"
              className={cn(
                'relative mx-auto h-full w-full',
                showTopbar && 'max-h-[calc(100vh - var(--topbar-height))]',
                className
              )}
            >
              {children}
            </div>
          </main>
        </SidebarInset>
      </div>
      <Toaster />
    </SidebarProvider>
  );
}
